export type HttpMethod = 'GET' | 'POST';

export interface HttpRequestOptions<TBody> {
  method?: HttpMethod;
  body?: TBody;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
  retries?: number;
  retryDelayMs?: number;
}

export interface HttpResponse<TData> {
  ok: boolean;
  status: number;
  data: TData;
}

interface RequestContext {
  attempt: number;
  maxAttempts: number;
  retryDelayMs: number;
}

const DEFAULT_TIMEOUT = 10000;
const DEFAULT_RETRY_DELAY = 500;
const DEFAULT_RETRIES = 0;

export class HttpClient {
  constructor(private readonly baseUrl: string) {}

  async request<TBody extends Record<string, string> | URLSearchParams | undefined, TResponse>(
    endpoint: string,
    options: HttpRequestOptions<TBody> = {}
  ): Promise<HttpResponse<TResponse>> {
    const {
      method = 'POST',
      body,
      headers = {},
      signal,
      timeoutMs = DEFAULT_TIMEOUT,
      retries = DEFAULT_RETRIES,
      retryDelayMs = DEFAULT_RETRY_DELAY
    } = options;

    const context: RequestContext = {
      attempt: 0,
      maxAttempts: retries + 1,
      retryDelayMs
    };

    return this.performRequest<TBody, TResponse>(endpoint, {
      method,
      body,
      headers,
      signal,
      timeoutMs,
      context
    });
  }

  private async performRequest<TBody extends Record<string, string> | URLSearchParams | undefined, TResponse>(
    endpoint: string,
    params: {
      method: HttpMethod;
      body?: TBody;
      headers: Record<string, string>;
      signal?: AbortSignal;
      timeoutMs: number;
      context: RequestContext;
    }
  ): Promise<HttpResponse<TResponse>> {
    const { method, body, headers, signal, timeoutMs, context } = params;

    context.attempt += 1;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method,
        headers,
        body: body instanceof URLSearchParams ? body : body ? new URLSearchParams(body) : undefined,
        signal: signal ?? controller.signal,
        credentials: 'same-origin'
      });

      clearTimeout(timeoutId);

      const data = await this.parseResponse<TResponse>(response);

      if (!response.ok && this.shouldRetry(response.status, context)) {
        await this.delay(context.retryDelayMs);
        return this.performRequest(endpoint, params);
      }

      return {
        ok: response.ok,
        status: response.status,
        data
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        if (this.shouldRetry(0, context)) {
          await this.delay(context.retryDelayMs);
          return this.performRequest(endpoint, params);
        }
      }

      throw error;
    }
  }

  private shouldRetry(status: number, context: RequestContext): boolean {
    const retriableStatus = [408, 425, 429, 500, 502, 503, 504];
    const hasAttemptsLeft = context.attempt < context.maxAttempts;

    return hasAttemptsLeft && (status === 0 || retriableStatus.includes(status));
  }

  private async parseResponse<TResponse>(response: Response): Promise<TResponse> {
    const contentType = response.headers.get('Content-Type') ?? '';

    if (contentType.includes('application/json')) {
      return (await response.json()) as TResponse;
    }

    const text = await response.text();
    try {
      return JSON.parse(text) as TResponse;
    } catch (error) {
      if (text.trim().startsWith('<')) {
        throw new Error('Servidor retornou HTML inesperado.');
      }
      throw error;
    }
  }

  private async delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
